import { FC } from 'react';
import { ChallengeCard, CardLoader, UiLibrary, Divider, Icon } from 'learning-panel-components';
import { translate } from 'translations';
import { coreService, tournamentServer } from 'network-utils';
import { getPlatformUrl } from 'utils';
import Gallery from '@components/assessments/gallery';
import TournamentCard from '@components/tournament/tournamentCard';
import TournamentBanner from '@components/tournament/tournamentBanner';
import PlayLeaderboard from './mainLeaderboard/playLeaderboard';
import styles from './play.module.scss';

const Play: FC<PlayProps> = props => {
    const { challenges, isLoading, setCurrentChallenge, upComingTournaments, pastTournaments, locale } = props;

    return (
        <div id="play-page" className={styles['content-wrapper']}>
            {upComingTournaments?.length > 0 && (
                <div className={styles.tournament}>
                    {upComingTournaments.length > 1 ? (
                        <div className={styles['section-gallery']}>
                            <Gallery elementWidth="35rem" numberOfElements={upComingTournaments.length}>
                                {upComingTournaments.map((tournament, i) => (
                                    <TournamentBanner
                                        {...tournament}
                                        locale={locale}
                                        participants={tournament.participants || []}
                                        key={i}
                                    />
                                ))}
                            </Gallery>
                        </div>
                    ) : (
                        <TournamentBanner
                            {...upComingTournaments[0]}
                            locale={locale}
                            participants={upComingTournaments[0].participants || []}
                        />
                    )}
                </div>
            )}
            <div className={styles.leaderboard}>
                <PlayLeaderboard />
            </div>
            <div className={styles.title}>
                <div className={styles.text}>{translate('AllChallenges')}</div>
                <Divider orientation="vertical" className={styles.divider} color="rgba(255, 255, 255, 0.5)" />
                <div className={styles.details}>{translate('CouldYouScoreTheMaximum')}</div>
                <Icon name="devCoin" className={styles['coin-icon']} />
                <div className={styles.details}>{translate('DevcoinsOfEachChallenge')}</div>
            </div>
            <div className={styles.list}>
                <UiLibrary.Grid container direction="row" justifyContent="flex-start" alignItems="center" spacing={5}>
                    {isLoading
                        ? new Array(20).fill('').map((_, i) => (
                              <UiLibrary.Grid item key={i}>
                                  <CardLoader width="16rem" height="15.34rem" />
                              </UiLibrary.Grid>
                          ))
                        : challenges?.map((challenge: coreService.ChallengeData) => (
                              <UiLibrary.Grid item key={challenge.uuid}>
                                  <ChallengeCard
                                      score={challenge?.highestScore}
                                      isTaken={challenge.taken}
                                      maxScore={challenge.maxScore}
                                      level={challenge.level}
                                      language={challenge.language}
                                      onShowLeaderboard={() => {
                                          setCurrentChallenge(challenge);
                                      }}
                                      name={challenge.displayName}
                                      imageSrc={`${process.env.PUBLIC_URL}/images/challengeBackground.svg`}
                                      challengeUrl={`${getPlatformUrl()}${challenge.url}`}
                                  />
                              </UiLibrary.Grid>
                          ))}
                </UiLibrary.Grid>
                {pastTournaments.length > 0 && (
                    <>
                        <div className={styles.text}>{translate('PastTournaments')}</div>

                        <Divider
                            className={styles['long-divider']}
                            orientation="horizontal"
                            color="rgba(255, 255, 255, 0.2)"
                        />
                        <UiLibrary.Grid
                            container
                            direction="row"
                            justifyContent="flex-start"
                            alignItems="center"
                            spacing={5}
                        >
                            {pastTournaments.map((pastTournament: tournamentServer.PastTournament) => (
                                <UiLibrary.Grid item key={pastTournament.uuid}>
                                    <TournamentCard
                                        name={pastTournament.name}
                                        isOver={true}
                                        rounds={pastTournament.numberOfRounds}
                                        participants={pastTournament.numParticipatedUsers}
                                        tournamentUrl={`/app/tournament?tournamentUuid=${pastTournament.uuid}`}
                                        date={new Date(pastTournament.dateTime)}
                                        score={pastTournament.userScore}
                                    />
                                </UiLibrary.Grid>
                            ))}
                        </UiLibrary.Grid>
                    </>
                )}
            </div>
        </div>
    );
};

export default Play;

interface PlayProps {
    isLoading?: boolean;
    challenges?: coreService.ChallengeData[];
    upComingTournaments: tournamentServer.UpComingTournament[];
    pastTournaments: tournamentServer.PastTournament[];
    setCurrentChallenge: Function;
    locale: string;
}
